import React, { useId, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import CategoryPill from './CategoryPill';
import { MAX_ATTRIBUTE_CATEGORIES, categoryDisplay, categoryKey } from './categoryTools';

const CONFIRM_LABELS = Object.freeze({
    rename: 'Rename',
    merge: 'Merge',
    clear: 'Clear category',
    none: 'Rename',
});

const NAME_HELP = 'Pick an existing category to merge into it, or leave the name empty to clear it.';

const formSx = { display: 'flex', flexDirection: 'column', minHeight: 0 };

function securities(count) {
    return `${count.toLocaleString()} ${count === 1 ? 'security' : 'securities'}`;
}

// Categories the renamed one can merge into: one spelling each, without the
// category being renamed and without blanks.
function mergeTargets(existingValues, fromKey) {
    const byKey = new Map();
    (existingValues || []).forEach((value) => {
        const key = categoryKey(value);
        if (key && key !== fromKey && !byKey.has(key)) byKey.set(key, categoryDisplay(value));
    });
    return [...byKey.values()];
}

// What confirming does with the typed name. Merging writes the existing
// category's spelling, and a blank (or "Unknown") name clears the category,
// as the backend would.
function planRename(fromDisplay, input, targets) {
    const toKey = categoryKey(input);
    if (!toKey) return { action: 'clear', toValue: '' };
    const toDisplay = categoryDisplay(input);
    if (toDisplay === fromDisplay) return { action: 'none', toValue: toDisplay };
    const existing = targets.find((target) => categoryKey(target) === toKey);
    if (existing) return { action: 'merge', toValue: existing };
    return { action: 'rename', toValue: toDisplay };
}

function RenameSummary({ id, plan, fromDisplay, rowCount }) {
    let text = null;
    if (rowCount <= 0) {
        text = 'No securities in this category can be changed.';
    } else if (plan.action === 'clear') {
        text = `Clear this category on ${securities(rowCount)}.`;
    } else if (plan.action !== 'none') {
        const [verb, preposition] = plan.action === 'merge' ? ['Merge', 'into'] : ['Rename', 'to'];
        text = (
            <>
                {verb} <strong>{fromDisplay}</strong> {preposition} <strong>{plan.toValue}</strong>
                {' '}on {securities(rowCount)}.
            </>
        );
    }
    return (
        <Typography id={id} variant="body2" sx={{ minHeight: '1.5em', overflowWrap: 'anywhere' }}>
            {text}
        </Typography>
    );
}

// Mounted per opening (and per category), so the name starts as the current one.
function CategoryRenameForm({
    titleId,
    fromValue,
    rowCount,
    existingValues,
    columnLabel,
    categoryCountAfter,
    onConfirm,
    onCancel,
}) {
    const fromDisplay = categoryDisplay(fromValue);
    const [input, setInput] = useState(fromDisplay);
    const summaryId = useId();
    const targets = useMemo(
        () => mergeTargets(existingValues, categoryKey(fromValue)),
        [existingValues, fromValue],
    );
    const plan = planRename(fromDisplay, input, targets);
    const canConfirm = rowCount > 0 && plan.action !== 'none';
    // Confirming stays possible above the limit: reducing existing categories
    // is a step towards a column that can be saved.
    const countAfter = canConfirm && categoryCountAfter ? categoryCountAfter(plan.toValue) : 0;

    const submit = (event) => {
        event.preventDefault();
        if (canConfirm) onConfirm(plan.toValue);
    };

    return (
        <Box component="form" noValidate onSubmit={submit} sx={formSx}>
            <DialogTitle id={titleId}>
                {columnLabel ? `Rename ${columnLabel} category` : 'Rename category'}
            </DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 1.5, pt: '12px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <CategoryPill value={fromValue} />
                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {securities(rowCount)}
                    </Typography>
                </Box>
                <Autocomplete
                    freeSolo
                    disableClearable
                    clearOnBlur={false}
                    options={targets}
                    inputValue={input}
                    onInputChange={(_event, next) => setInput(next)}
                    renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        return (
                            <li key={key} {...optionProps}>
                                <CategoryPill value={option} />
                            </li>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            autoFocus
                            size="small"
                            label="New name"
                            helperText={NAME_HELP}
                            onFocus={(event) => event.target.select()}
                            onKeyDown={(event) => {
                                // A free-text name leaves an empty suggestion list
                                // "open", which would swallow Escape. Escape closes a
                                // showing list first, otherwise the dialog.
                                const listShowing = event.target.getAttribute('aria-expanded') === 'true';
                                if (event.key === 'Escape' && !listShowing) {
                                    event.stopPropagation();
                                    onCancel();
                                }
                            }}
                        />
                    )}
                />
                <RenameSummary id={summaryId} plan={plan} fromDisplay={fromDisplay} rowCount={rowCount} />
                {countAfter > MAX_ATTRIBUTE_CATEGORIES && (
                    <Alert severity="warning" sx={{ py: 0.25 }}>
                        {`${columnLabel || 'This column'} would have ${countAfter} categories. At most ${MAX_ATTRIBUTE_CATEGORIES} can be saved, so merge or clear more before saving.`}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onCancel()}>Cancel</Button>
                <Button
                    type="submit"
                    variant="contained"
                    color={plan.action === 'clear' ? 'error' : 'primary'}
                    disabled={!canConfirm}
                    aria-describedby={summaryId}
                >
                    {CONFIRM_LABELS[plan.action]}
                </Button>
            </DialogActions>
        </Box>
    );
}

/**
 * Renames one category on every row that holds it, merges it into an existing
 * category, or clears it (an empty name). `rowCount` is how many rows will
 * change; onConfirm(toValue) receives the value to write ('' to clear).
 * Given `categoryCountAfter(toValue)`, the number of categories the column
 * would hold afterwards, the dialog warns when that is over the limit.
 */
export default function CategoryRenameDialog({
    open,
    fromValue,
    rowCount,
    existingValues,
    columnLabel,
    categoryCountAfter,
    onConfirm,
    onCancel,
}) {
    const titleId = useId();
    return (
        <Dialog open={open} onClose={() => onCancel()} maxWidth="xs" fullWidth aria-labelledby={titleId}>
            <CategoryRenameForm
                key={categoryDisplay(fromValue)}
                titleId={titleId}
                fromValue={fromValue}
                rowCount={rowCount}
                existingValues={existingValues}
                columnLabel={columnLabel}
                categoryCountAfter={categoryCountAfter}
                onConfirm={onConfirm}
                onCancel={onCancel}
            />
        </Dialog>
    );
}

RenameSummary.propTypes = {
    id: PropTypes.string.isRequired,
    plan: PropTypes.shape({ action: PropTypes.string, toValue: PropTypes.string }).isRequired,
    fromDisplay: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const sharedPropTypes = {
    fromValue: PropTypes.string,
    rowCount: PropTypes.number.isRequired,
    existingValues: PropTypes.arrayOf(PropTypes.string),
    columnLabel: PropTypes.string,
    categoryCountAfter: PropTypes.func,
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

CategoryRenameForm.propTypes = {
    ...sharedPropTypes,
    titleId: PropTypes.string.isRequired,
};

CategoryRenameDialog.propTypes = {
    ...sharedPropTypes,
    open: PropTypes.bool.isRequired,
};
